<script setup lang="ts">
import {
    EMPLOYER_PROFILE_COLUMNS_LARGE_DEVICES,
    EMPLOYER_PROFILE_COLUMNS_SMALL_DEVICES,
    FEATURES as PRODUCT_COMPARISON_FEATURES,
} from '@b2b-platform/commerce/texts/product-comparison-employer-profile';
import { formatPrice } from '@b2b-platform/commerce/utils/format';
import resolveConfig from 'tailwindcss/resolveConfig';

import tailwindConfig from '~/packages/base/tailwind.config';
import { useContactRequestForm } from '~/packages/commerce/composables/useContactRequestForm';
import { EMPLOYER_PROFILE_FAQ_ITEMS } from '~/packages/commerce/texts/faqs';
import {
    type Product,
    ProductCategory,
} from '~/packages/commerce/types/product';

definePageMeta({
    isAuthRequired: false,
    layout: 'content',
});

const seoTitle = 'Arbeitgeberprofil für Ihre Marke';
const seoDescription =
    'Passende Lösungen für Ihr Unternehmen ☛ Stärken Sie Ihre Arbeitgebermarke & heben Sie sich damit von anderen Arbeitgebern ab.';

useSeoMeta({
    description: seoDescription,
    ogDescription: seoDescription,
    ogTitle: seoTitle,
    title: seoTitle,
});

useRobotsRule(true);

const fullConfig = resolveConfig(tailwindConfig as any);
const isDesktopDevice = useMatchMedia(
    `(min-width: ${fullConfig.theme.screens.xl})`,
);

const productComparisonColumns = ref(
    isDesktopDevice.value
        ? EMPLOYER_PROFILE_COLUMNS_LARGE_DEVICES
        : EMPLOYER_PROFILE_COLUMNS_SMALL_DEVICES,
);
const { data: products, status } = await useLazyFetch<Product[]>(
    `/api/commerce/products/${ProductCategory.EMPLOYER_PROFILE}`,
);

const citations = computed<string[]>(() => {
    return [
        'Marketagent für karriere.at (2023)',
        'karriere.at behält sich das Recht vor, die Online-Präsenz entsprechend zu verkürzen, wenn durch den Kunden ' +
            'Verzögerungen entstehen, beispielsweise durch verspätete Freigaben oder die verzögerte Bereitstellung von Informationen.',
    ];
});

const getCitationIndex = (description: string) =>
    citations.value.indexOf(description) + 1;

const EMPLOYER_BRANDING_BENEFITS = [
    {
        description:
            'Mit einem Arbeitgeberprofil auf karriere.at bleibt Ihr Unternehmen sichtbar und stärkt langfristig seine Markenbekanntheit als attraktiver Arbeitgeber.',
        headline: 'Sichtbarkeit & langfristige Markenbildung',
        icon: 'devices',
    },
    {
        description:
            'Indem Sie Einblicke in Ihre Unternehmenskultur geben, ziehen Sie Bewerbende an, die zu Ihnen passen & verbessern so die Bewerbungsqualität.',
        headline: 'Passende Talente anziehen',
        icon: 'user-group',
    },
    {
        description:
            'Ein authentisches Arbeitgeberprofil hilft Bewerbenden schnell zu erkennen, ob sie zueinander passen, und minimiert Fehleinstellungen.',
        headline: 'Klarheit schaffen, Fehleinstellungen vermeiden',
        icon: 'lightbulb-on',
    },
];

const { open: openContactRequestForm } = useContactRequestForm();
</script>

<template>
    <div>
        <section
            class="mx-auto grid max-w-content grid-cols-3 justify-between gap-7 px-5 pb-7 pt-11 lg:px-7"
        >
            <div class="col-span-3 flex flex-col gap-6 lg:col-span-2">
                <div class="flex flex-col gap-3">
                    <BaseHeadline
                        as="h1"
                        underline
                        text="Arbeitgeberprofil"
                        class="text-left"
                    >
                        Arbeitgeberprofil
                    </BaseHeadline>
                    <JamBaseText>
                        Mit den Arbeitgeberprofilen von karriere.at stärken Sie
                        Ihre Arbeitgebermarke und geben Jobsuchenden Einblicke
                        in Ihr Unternehmen.
                    </JamBaseText>
                </div>
                <div class="grid grid-cols-1 gap-7 md:grid-cols-2">
                    <div>
                        <JamBaseText variant="3xl"> 83 % </JamBaseText>
                        <JamBaseText>
                            der Talente ist es wichtig, einen Einblick hinter
                            die Kulissen eines potenziellen Arbeitgebers vor
                            einer Bewerbung zu bekommen.
                            <CommBaseCitationLink :number="1" />
                        </JamBaseText>
                    </div>
                    <div>
                        <JamBaseText variant="3xl">Jede*r Dritte</JamBaseText>
                        <JamBaseText>
                            hat sich schon einmal nicht auf eine
                            Stellenausschreibung beworben, weil zu wenige
                            Information über ein Unternehmen verfügbar waren.
                            <CommBaseCitationLink :number="1" />
                        </JamBaseText>
                    </div>
                </div>
            </div>
            <div>
                <NuxtImg
                    src="/images/employer-profile/hero-desktop-mobile.png"
                    width="500"
                    height="271"
                    loading="lazy"
                    decoding="auto"
                    alt="Arbeitgeberprofil Plus mit Keyfact Box, Headerbild und den wichtigsten Daten zur Firma."
                    class="hidden px-10 py-6 lg:block"
                />
            </div>

            <CommCampaignBannerProductOverview class="col-span-full mt-7" />
        </section>
        <section class="bg-gray-50">
            <div
                class="mx-auto grid max-w-content grid-cols-1 gap-x-7 gap-y-6 p-5 md:grid-cols-3 md:gap-x-5 md:gap-y-0 md:py-5 xl:gap-x-7"
            >
                <template v-if="status === 'pending'">
                    <CommProductCard
                        v-for="index in 3"
                        :key="`loading-product-card-${index}`"
                        :is-loading="true"
                        class="xl:row-start-1 xl:row-end-3 xl:grid xl:grid-rows-subgrid"
                    >
                        <template #performance>
                            <div
                                :class="`rounded-t row-start-1 px-6 py-4 text-center text-sm text-white`"
                            >
                                <JamBaseSkeletonLoader
                                    height="h:[16px] md:h-[16px] lg:h-[16px]"
                                />
                            </div>
                        </template>
                    </CommProductCard>
                </template>
                <template v-else>
                    <CommProductCard
                        v-for="product in products"
                        :key="product.id"
                        :class="`md:row-start-1 md:row-end-3 md:grid md:grid-rows-subgrid`"
                        :headline="product.name"
                        :sub-headline="product.description"
                        :can-be-purchased-online="
                            !product.features?.['service-price']
                        "
                        :detail-page-url="`/produkte/arbeitgeberprofil/${product.mainProductVariantId}`"
                        :image-src="
                            product.media?.['product-illustration']?.[0]
                                ?.medium_url
                        "
                        :image-alt="
                            product.media?.['product-illustration']?.[0]?.alt
                        "
                        :features="product.features['teaser-list']"
                        gtm-element="Employer Profile Purchase"
                        top-area-clases="min-h-[332px]"
                    >
                        <template #performance>
                            <div
                                :class="`rounded-t row-start-1 ${
                                    product.features.performance?.[0].value ??
                                    'hidden md:flex md:opacity-0'
                                } px-6 py-4 text-center text-sm text-white`"
                            >
                                <span
                                    v-html="
                                        product.features.performance?.[0]?.label
                                    "
                                />
                            </div>
                        </template>

                        <template
                            v-if="product.features?.['service-price']"
                            #price
                        >
                            ab
                            <JamBaseText variant="xl" as="span">
                                {{
                                    product.features?.['service-price']?.[0]
                                        ?.value
                                }}
                            </JamBaseText>
                        </template>
                        <template
                            v-else-if="
                                product.variants?.data[0]?.priceTiers?.length >
                                0
                            "
                            #price
                        >
                            <JamBaseText variant="xl" as="span">
                                {{
                                    formatPrice(
                                        product.variants?.data[0]?.priceTiers[0]
                                            ?.price.amount,
                                    )
                                }}
                            </JamBaseText>
                        </template>
                    </CommProductCard>
                </template>

                <CommBaseCollapse
                    class="col-span-1 mx-auto w-full max-w-content md:col-span-3 md:row-start-3 md:py-6"
                    trigger-classes="!justify-center !gap-5"
                >
                    <template #trigger>
                        <JamBaseLink variant="gray" as="button">
                            Vollständiger Produktvergleich
                        </JamBaseLink>
                    </template>
                    <CommBaseTable
                        class="mt-6"
                        :columns="productComparisonColumns"
                    >
                        <template #body>
                            <CommBaseTableSection
                                v-for="(
                                    productComparisonFeature, index
                                ) in PRODUCT_COMPARISON_FEATURES"
                                :key="`comparison-section-${index}`"
                                :title="productComparisonFeature.title"
                                :rows="productComparisonFeature.rows"
                            />
                        </template>
                    </CommBaseTable>
                </CommBaseCollapse>
            </div>
        </section>
        <div class="mx-auto max-w-content px-5 lg:px-7">
            <section class="mt-11 flex flex-col gap-6">
                <BaseHeadline
                    as="h2"
                    variant="h3"
                    underline
                    text="Starkes Arbeitgeberprofil für einen langfristigen
                    Recruiting-Erfolg"
                >
                    Starkes Arbeitgeberprofil für einen langfristigen
                    Recruiting-Erfolg
                </BaseHeadline>
                <div class="grid grid-cols-1 gap-7 md:grid-cols-3">
                    <div
                        v-for="(item, index) in EMPLOYER_BRANDING_BENEFITS"
                        :key="`feature-employer-branding-${index}`"
                        class="flex flex-col gap-2"
                    >
                        <JamBaseIcon
                            :icon-name="item.icon"
                            stroke="normal"
                            size="xl"
                            class="text-gray-500"
                        />
                        <JamBaseText variant="large">
                            {{ item.headline }}
                        </JamBaseText>
                        <JamBaseText>
                            {{ item.description }}
                        </JamBaseText>
                    </div>
                </div>
            </section>
            <section
                class="mt-11 flex flex-col items-start gap-12 rounded bg-green-50 p-6 md:p-8 lg:flex-row lg:items-center lg:justify-between xl:mx-16"
            >
                <div class="flex flex-col gap-5">
                    <JamBaseText variant="xl">
                        So könnte Ihre branding.solution aussehen
                    </JamBaseText>
                    <JamBaseButton
                        :full-width="!isDesktopDevice"
                        @click="
                            openContactRequestForm();
                            navigateTo('#contact-request');
                        "
                    >
                        Unverbindlich anfragen
                    </JamBaseButton>
                </div>
                <BaseYoutubeVideo
                    video-id="6mrH9df-8Nw"
                    class="order-first md:order-last md:!w-[500px]"
                />
            </section>

            <CommFaqSection
                class="mt-14"
                :faq-items="EMPLOYER_PROFILE_FAQ_ITEMS"
            />

            <MainCitations :citations="citations" />
        </div>
        <MainContact />
    </div>
</template>
